import React, { useState, useCallback, useMemo } from 'react';
import { getAdmins, createAdmin, updateAdmin, deleteAdmin } from '../services/api';
import { useApiQuery, useApiMutation } from '../hooks/useApiQuery';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  XMarkIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const AdminManagement = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin',
    status: 'inactive',
  });

  // ✅ React Query: Fetch admins
  const {
    data: admins = [],
    isLoading,
    isError,
    refetch: refetchAdmins,
  } = useApiQuery(
    ['admins'],
    () => getAdmins().then(res => res.data),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      onError: () => {
        console.error('Failed to load admins');
      },
    }
  );

  // ✅ React Query: Create admin mutation
  const createMutation = useApiMutation(
    (data) => createAdmin(data).then(res => res.data),
    {
      onSuccess: () => {
        handleCloseModal();
        refetchAdmins();
      },
      onError: (error) => {
        console.error('Error creating admin:', error);
        alert('Error creating admin: ' + (error.response?.data?.message || 'Unknown error'));
      },
    }
  );

  // ✅ React Query: Update admin mutation
  const updateMutation = useApiMutation(
    ({ id, data }) => updateAdmin(id, data).then(res => res.data),
    {
      onSuccess: () => {
        handleCloseModal();
        refetchAdmins();
      },
      onError: (error) => {
        console.error('Error updating admin:', error);
        alert('Error updating admin: ' + (error.response?.data?.message || 'Unknown error'));
      },
    }
  );

  // ✅ React Query: Delete admin mutation
  const deleteMutation = useApiMutation(
    (id) => deleteAdmin(id).then(res => res.data),
    {
      onSuccess: () => {
        refetchAdmins();
      },
      onError: (error) => {
        console.error('Error deleting admin:', error);
        alert('Error deleting admin');
      },
    }
  );

  // ✅ Calculate summary stats (memoized)
  const stats = useMemo(() => {
    const totalAdmins = admins.length;
    const activeAdmins = admins.filter(a => a.status === 'active').length;
    const superAdmins = admins.filter(a => a.role === 'super_admin').length;
    return { totalAdmins, activeAdmins, superAdmins };
  }, [admins]);

  // ✅ Modal handlers
  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingAdmin(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'admin',
      status: 'inactive',
    });
  }, []);

  const handleEdit = useCallback((admin) => {
    setEditingAdmin(admin);
    setFormData({
      name: admin.name,
      email: admin.email,
      password: '',
      role: admin.role,
      status: admin.status,
    });
    setShowModal(true);
  }, []);

  // ✅ Submit handler
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (editingAdmin) {
      updateMutation.mutate({ id: editingAdmin._id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  }, [editingAdmin, formData, createMutation, updateMutation]);

  // ✅ Delete handler
  const handleDelete = useCallback((id) => {
    if (window.confirm('Are you sure you want to delete this admin?')) {
      deleteMutation.mutate(id);
    }
  }, [deleteMutation]);

  // ✅ Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading admins...</p>
        </div>
      </div>
    );
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const isDeleting = deleteMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Management</h1>
          <p className="text-sm text-gray-500 mt-1">Manage system administrators and their permissions</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 bg-[#1672b9] text-white px-5 py-2.5 rounded-lg text-sm font-medium shadow-sm hover:bg-[#0f5a9e] transition-all duration-200 disabled:opacity-50"
        >
          <PlusIcon className="w-4 h-4" />
          Add New Admin
        </button>
      </div>

      {/* Error Alert */}
      {isError && (
        <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-lg text-sm">
          Failed to load admins. Please refresh the page.
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
          <div className="p-3 bg-[#1672b9]/10 rounded-xl">
            <UserGroupIcon className="w-6 h-6 text-[#1672b9]" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Total Admins</p>
            <p className="text-2xl font-semibold text-gray-900">{stats.totalAdmins}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
          <div className="p-3 bg-green-50 rounded-xl">
            <CheckCircleIcon className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Active</p>
            <p className="text-2xl font-semibold text-gray-900">{stats.activeAdmins}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
          <div className="p-3 bg-[#1672b9]/10 rounded-xl">
            <ClockIcon className="w-6 h-6 text-[#1672b9]" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Super Admins</p>
            <p className="text-2xl font-semibold text-gray-900">{stats.superAdmins}</p>
          </div>
        </div>
      </div>

      {/* Admins Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-medium text-gray-900">All Administrators</h2>
          <button
            onClick={() => refetchAdmins()}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 disabled:opacity-50"
            title="Refresh"
          >
            <ArrowPathIcon className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {admins.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-12">
                    <p className="text-gray-500">No administrators found. Click "Add New Admin" to create one.</p>
                  </td>
                </tr>
              ) : (
                admins.map((admin) => (
                  <tr key={admin._id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{admin.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{admin.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        admin.role === 'super_admin'
                          ? 'bg-[#1672b9]/10 text-[#1672b9]'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {admin.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        admin.status === 'active'
                          ? 'bg-green-50 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        {admin.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(admin.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(admin)}
                        disabled={isDeleting || isSubmitting}
                        className="text-gray-400 hover:text-[#1672b9] transition-colors mr-4 disabled:opacity-50"
                        title="Edit"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(admin._id)}
                        disabled={isDeleting || isSubmitting}
                        className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                        title="Delete"
                      >
                        {isDeleting && deleteMutation.variables === admin._id ? (
                          <span className="inline-block w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></span>
                        ) : (
                          <TrashIcon className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal for Add/Edit Admin */}
      {showModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-lg font-medium text-gray-900">
                {editingAdmin ? 'Edit Administrator' : 'Add New Administrator'}
              </h3>
              <button
                onClick={handleCloseModal}
                disabled={isSubmitting}
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                  required
                  disabled={isSubmitting}
                />
              </div>

              {!editingAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                    required
                    disabled={isSubmitting}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                  disabled={isSubmitting || (editingAdmin && editingAdmin.role === 'super_admin')}
                >
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
                {editingAdmin && editingAdmin.role === 'super_admin' && (
                  <p className="text-xs text-gray-500 mt-1">Cannot change role of super admin</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                  disabled={isSubmitting}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-[#1672b9] text-white rounded-lg text-sm font-medium hover:bg-[#0f5a9e] transition-all shadow-sm disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving...' : (editingAdmin ? 'Update Admin' : 'Create Admin')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;